import './FeaturesPage.css';

import React from 'react';

import { useNavigate } from 'react-router-dom';

import MarketingFooter
  from '../components/common/MarketingFooter/MarketingFooter';
import MarketingTopNav
  from '../components/common/MarketingTopNav/MarketingTopNav';
import PomPom from '../components/common/PomPom/PomPom';
import { colors } from '../styles/colorPalette';

const ACCENT = colors.brand.accentPink;
const MAROON = colors.primary.main;

function ScreenshotPlaceholder({
  label,
  imageSrc,
  tall = false,
}: {
  label: string;
  imageSrc?: string;
  tall?: boolean;
}) {
  if (imageSrc) {
    return (
      <div className={`fp1-screenshot ${tall ? 'fp1-screenshot--tall' : ''}`}>
        <img className="fp1-screenshot-image" src={imageSrc} alt={label} />
      </div>
    );
  }

  return (
    <div className={`fp1-screenshot ${tall ? 'fp1-screenshot--tall' : ''}`}>
      <div className="fp1-screenshot-icon">📱</div>
      <div className="fp1-screenshot-label">{label}</div>
    </div>
  );
}

const features = [
  {
    tag: 'Not just numbers',
    title: 'See who you\'re scoring',
    body: 'No one can keep track of tryout numbers while flipping between paper sheets. See detailed athlete views and star the athletes you\'re watching to switch back and forth with ease.',
    screenshot: 'Athlete Screen - Athlete Card',
    screenshotImage: '/images/featuresPage/athlete-card-and-tabs.webp',
    accent: MAROON,
  },
  {
    tag: 'No accounts, no friction',
    title: 'Coaches join in seconds.',
    body: 'Share the tryout code, and scoring coaches open the link in any browser. No app download, no sign-up.',
    screenshot: 'Join screen — code entry',
    screenshotImage: '/images/featuresPage/coach_join_page.webp',
    accent: ACCENT,
  },
  {
    tag: 'Everything on one sheet',
    title: 'Photos, Videos, Skills, and Comments.',
    body: 'Score each skill, snap a photo, record a clip, and leave notes - all on a single athlete page. Optimized for phone screens.',
    screenshot: 'Scoring screen — athlete card',
    screenshotImage: '/images/featuresPage/photos_air_positions.webp',
    accent: MAROON,
  },
  // {
  //   tag: 'Stay in the flow.',
  //   title: 'Jump between athletes instantly.',
  //   body: 'The quick-select bar keeps every athlete a single tap away. No back button, no losing your place mid-score.',
  //   screenshot: 'Quick-select tab bar',
  //   accent: colors.brand.accentTeal,
  // },
  {
    tag: 'Your tryout, your criteria',
    title: 'Custom forms, your way.',
    body: 'Build a scoring form with the skills that matter to your program. Name them, order them. Change it next season.',
    screenshot: 'Form builder screen',
    screenshotImage: '/images/featuresPage/forms.webp',
    accent: ACCENT,
  },
  {
    tag: 'Easier choices',
    title: 'Team placements made easy',
    body: 'Review scores with your coaches, and place athletes into teams with a tap.',
    screenshot: 'Athlete placement screen',
    screenshotImage: '/images/featuresPage/placements.webp',
    accent: MAROON,
  },
  {
    tag: 'Less yelling',
    title: 'Send notifications',
    body: 'Make announcements to the your coaching staff at once.',
    screenshot: 'Coaches announcements notification',
    screenshotImage: '/images/featuresPage/announcements.webp',
    accent: MAROON,
  },
];

const FeaturesPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="fp1-container">
      <MarketingTopNav />

      <div className="fp1-gutter-pompoms" aria-hidden="true">
        <PomPom size="xlarge" color="pink" className="fp1-gutter-pompom fp1-gutter-pompom-right-top" />
        <PomPom size="large" color="teal" className="fp1-gutter-pompom fp1-gutter-pompom-left-mid" />
        <PomPom size="xlarge" color="teal" className="fp1-gutter-pompom fp1-gutter-pompom-right-bottom" />
        <PomPom size="medium" color="pink" className="fp1-gutter-pompom fp1-gutter-pompom-left-bottom" />
      </div>

      <div className="fp1-maxwidth">
        <section className="fp1-hero">
          <div className="fp1-hero-inner">
            <div className="fp1-hero-title-row">
              <h1 className="fp1-hero-title">
                Everything your<br />
                <span className="fp1-hero-accent">tryout needs</span>
              </h1>
              <div className="fp1-hero-title-pompoms" aria-hidden="true">
                <PomPom size="large" color="pink" className="fp1-hero-pompom fp1-hero-pompom-front" />
                <PomPom size="medium" color="teal" className="fp1-hero-pompom fp1-hero-pompom-back" />
              </div>
            </div>
          </div>
        </section>

        <section className="fp1-features">
          <h2 className="fp1-section-title">
            Built for the <span style={{ color: ACCENT }}>mat.</span>
          </h2>

          {features.map((feature, index) => (
            <div key={feature.title} className={`fp1-feature ${index % 2 === 1 ? 'fp1-feature--flip' : ''}`}>
              <div className="fp1-feature-text">
                <div className="fp1-feature-tag" style={{ color: feature.accent, borderColor: feature.accent }}>
                  {feature.tag}
                </div>
                <h3 className="fp1-feature-title">{feature.title}</h3>
                <p className="fp1-feature-body">{feature.body}</p>
              </div>
              <ScreenshotPlaceholder
                label={feature.screenshot}
                imageSrc={feature.screenshotImage}
              />
            </div>
          ))}
        </section>

        {/* <section className="fp1-steps">
          <div className="fp1-steps-header">
            <h2 className="fp1-steps-title">
              Four steps. <span style={{ color: ACCENT }}>Teams made.</span>
            </h2>
            <p className="fp1-steps-sub">From setup to squad in one tryout day.</p>
          </div>

          <div className="fp1-steps-grid">
            {steps.map((step) => (
              <div key={step.n} className="fp1-step-card">
                <div className="fp1-step-num" style={{ background: step.c }}>{step.n}</div>
                <h3 className="fp1-step-name">{step.t}</h3>
                <p className="fp1-step-desc">{step.d}</p>
              </div>
            ))}
          </div>
        </section> */}

        <section className="fp1-cta">
          <h2 className="fp1-cta-title">Ready to run your best tryout?</h2>
          <div className="fp1-cta-buttons">
            <button className="fp1-btn-primary" onClick={() => navigate('/createaccount')}>
              Get Started
            </button>
            <button className="fp1-btn-ghost" onClick={() => navigate('/demo')}>
              See a Demo →
            </button>
          </div>
        </section>  

        <MarketingFooter />
      </div>
    </div>
  );
};

export default FeaturesPage;
